export const unwrapApiResponse = (response) => ({
  data: response?.data?.data ?? response?.data ?? null,
  pagination: response?.data?.pagination ?? null,
  message: response?.data?.message ?? '',
});

const flattenErrorValue = (value, prefix = '') => {
  if (value == null) return [];
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return [`${prefix}${String(value)}`];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => flattenErrorValue(item, prefix));
  }
  if (typeof value === 'object') {
    return Object.entries(value).flatMap(([key, item]) =>
      flattenErrorValue(item, prefix ? `${prefix}${key}: ` : `${key}: `),
    );
  }
  return [];
};

export const getApiErrorMessage = (error, fallback = 'Something went wrong.') => {
  const payload = error?.response?.data;
  if (!payload) return error?.message || fallback;

  if (typeof payload === 'string') return payload;
  if (payload.detail) return String(payload.detail);
  if (payload.message && !payload.errors) return String(payload.message);

  const details = flattenErrorValue(payload.errors || payload);
  return details.length ? details.join(' • ') : payload.message || fallback;
};

export const formatDate = (value, options = {}) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...options,
  }).format(date);
};

export const formatDateTime = (value) =>
  formatDate(value, { hour: '2-digit', minute: '2-digit' });

export const humanize = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const filenameFromDisposition = (disposition) => {
  if (!disposition) return null;
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1].replace(/["']/g, ''));
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return match?.[1] || null;
};

export const downloadBlobResponse = (response, fallbackName = 'download.pdf') => {
  const blob = response?.data instanceof Blob ? response.data : new Blob([response?.data]);
  const filename =
    filenameFromDisposition(response?.headers?.['content-disposition']) || fallbackName;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const createBlobUrl = (response) => {
  const blob = response?.data instanceof Blob ? response.data : new Blob([response?.data]);
  return URL.createObjectURL(blob);
};

export const compactParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== '' && value != null),
  );

export const jobIsRunning = (status) => ['PENDING', 'PROCESSING'].includes(status);

export const documentTypeForOwner = (ownerType) =>
  ownerType === 'STAFF' ? 'STAFF_ID_CARD' : 'STUDENT_ID_CARD';
